
import { useState, useEffect, useRef } from "react";
import { useTranslations } from "use-intl";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Invoice } from "@/types/payments";
import ApprovalsInvoicesTable from "@/components/approvals/ApprovalsInvoicesTable";
import BulkApprovals from "@/components/approvals/BulkApprovals";
import { sortInvoices } from "@/utils/invoiceSortingUtils";
import { formatCurrency } from "@/utils/paymentUtils";
import { toast } from "sonner";
import { runRefreshWithFeedback } from "@/lib/refreshFeedback";
import { Loader2, Upload, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ServiceRegistry } from "@/services/ServiceRegistry";

const AccountPayablesApprovals = () => {
  const t = useTranslations("consultantApprovals.page");
  // The latest translator, so the one-time loader below can word its error without being re-run.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("to-be-approved");
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<keyof Invoice>("dueDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [pendingInvoices, setPendingInvoices] = useState<Invoice[]>([]);
  const [approvedInvoices, setApprovedInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  
  // Fetch invoices from API
  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        // Fetch from API using ApprovalService
        const [pendingResponse, approvedResponse] = await Promise.all([
          ServiceRegistry.getApprovalService().getUnapprovedInvoices(),
          ServiceRegistry.getApprovalService().getApprovedInvoices(),
        ]);
        setPendingInvoices(pendingResponse);
        setApprovedInvoices(approvedResponse);
      } catch (error) {
        console.error("Error fetching invoices:", error);
        toast.error(tRef.current("toasts.connectionError"), {
          description: tRef.current("toasts.loadFailed"),
        });
        setPendingInvoices([]);
        setApprovedInvoices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, []);
  
  const handleSort = (key: keyof Invoice) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (key: keyof Invoice) => {
    if (sortBy !== key) return null;
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  const toggleSelectInvoice = (invoiceId: string) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const toggleSelectAll = (invoices: Invoice[]) => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map(inv => inv.id));
    }
  };

  // Handle navigation to upload invoice page
  const handleUploadClick = () => {
    navigate('/upload');
  };
  
  // Handle refresh of all data
  const handleRefreshClick = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    try {
      await runRefreshWithFeedback(async () => {
        setLoading(true);
        const [pendingResponse, approvedResponse] = await Promise.all([
          ServiceRegistry.getApprovalService().getUnapprovedInvoices(),
          ServiceRegistry.getApprovalService().getApprovedInvoices(),
        ]);
        setPendingInvoices(pendingResponse);
        setApprovedInvoices(approvedResponse);
        setLoading(false);
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleApprove = async (invoiceId: string) => {
    try {
      const result = await ServiceRegistry.getApprovalService().approveInvoice(invoiceId);
      if (result.success) {
        setPendingInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
        setApprovedInvoices(prev => [...prev, result.invoice!]);
        toast.success(t("toasts.approvedTitle"), { description: t("toasts.approvedBody", { id: invoiceId }) });
      } else {
        throw new Error("Failed to approve invoice");
      }
    } catch (error) {
      console.error(`Error approving invoice: ${invoiceId}`, error);
      toast.error(t("toasts.approveFailedTitle"), {
        description: t("toasts.approveFailedBody"),
      });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedInvoices.length === 0) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        selectedInvoices.map(invoiceId => ServiceRegistry.getApprovalService().approveInvoice(invoiceId))
      );
      const successful = results.filter(r => r.success).length;
      const failed = results.length - successful;
      if (successful > 0) {
        const approvedIds = selectedInvoices.filter((_, index) => results[index].success);
        const newlyApproved = results
          .filter(r => r.success && r.invoice)
          .map(r => r.invoice!);
        setPendingInvoices(prev => prev.filter(inv => !approvedIds.includes(inv.id)));
        setApprovedInvoices(prev => [...prev, ...newlyApproved]);
        toast.success(t("toasts.bulkApprovedTitle"), {
          description: t("toasts.bulkApprovedBody", { successful, failed }),
        });
      }
      if (failed > 0) {
        toast.error(t("toasts.someFailedTitle"), {
          description: t("toasts.someFailedBody", { failed }),
        });
      }
    } catch (error) {
      console.error(`Error in bulk approve:`, error);
      toast.error(t("toasts.bulkFailedTitle"), {
        description: t("toasts.bulkFailedBody"),
      });
    } finally {
      setSelectedInvoices([]);
      setLoading(false);
    }
  };

  const sortedPendingInvoices = sortInvoices(pendingInvoices, sortBy, sortOrder);
  const sortedApprovedInvoices = sortInvoices(approvedInvoices, sortBy, sortOrder);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">{t("title")}</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleUploadClick}
            className="flex items-center gap-2"
          >
            <Upload size={18} />
            {t("upload")}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleRefreshClick}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
            {t("refresh")}
          </Button>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>{t("cardTitle")}</CardTitle>
          <CardDescription>{t("cardDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-lg text-muted-foreground">{t("loading")}</span>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="to-be-approved">{t("tabs.toBeApproved")}</TabsTrigger>
                <TabsTrigger value="approved">{t("tabs.approved")}</TabsTrigger>
              </TabsList>
              
              <TabsContent value="to-be-approved" className="space-y-4">
                <BulkApprovals 
                  selectedInvoices={selectedInvoices}
                  handleBulkApprove={handleBulkApprove}
                />
                <ApprovalsInvoicesTable 
                  invoices={sortedPendingInvoices}
                  selectedInvoices={selectedInvoices}
                  toggleSelectInvoice={toggleSelectInvoice}
                  toggleSelectAll={toggleSelectAll}
                  handleSort={handleSort}
                  renderSortIcon={renderSortIcon}
                  handleApprove={handleApprove}
                  formatCurrency={formatCurrency}
                  isPendingTab={true}
                />
              </TabsContent>
              
              <TabsContent value="approved" className="space-y-4">
                <ApprovalsInvoicesTable 
                  invoices={sortedApprovedInvoices}
                  selectedInvoices={[]}
                  toggleSelectInvoice={() => {}}
                  toggleSelectAll={() => {}}
                  handleSort={handleSort}
                  renderSortIcon={renderSortIcon}
                  handleApprove={() => {}}
                  formatCurrency={formatCurrency}
                  isPendingTab={false}
                />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountPayablesApprovals;
